/**
 * Derwent Links — public /DerwentLinks page.
 *
 * Modern editorial mobile-first bio-link page:
 *   • Full-bleed hero image at top (fades into background)
 *   • Bold display name with optional verified badge, handle, bio
 *   • Circular social icons row (Instagram / TikTok / FB / LinkedIn / YouTube)
 *   • Horizontal-scroll category pill chips
 *   • 2-column rich link card grid with optional cover images
 *   • Featured card spans full width (or both columns) with strong contrast
 *
 * Loads config from /api/derwent-links/public (no auth).
 * Tracks page-views + link clicks via /api/derwent-links/track.
 * Injects Meta / TikTok / Google Analytics pixels when IDs are configured.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import {
  Instagram, Facebook, Linkedin, Youtube, Music2,
  Phone, Mail, BadgeCheck, ArrowUpRight,
  Calendar, FileSearch, UploadCloud, Repeat, Home,
  TrendingDown, Briefcase, Landmark, CalendarCheck,
  Sparkles, Star, CheckCircle2, ChevronRight,
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// Icon library for link cards
const ICON_MAP = {
  'calendar-check': CalendarCheck,
  calendar: Calendar,
  'file-search': FileSearch,
  'upload-cloud': UploadCloud,
  repeat: Repeat,
  home: Home,
  'trending-down': TrendingDown,
  briefcase: Briefcase,
  landmark: Landmark,
  phone: Phone,
  mail: Mail,
  sparkles: Sparkles,
  star: Star,
  'check-circle-2': CheckCircle2,
};

const SOCIAL_META = {
  instagram: { Icon: Instagram, label: 'Instagram' },
  tiktok: { Icon: Music2, label: 'TikTok' },
  facebook: { Icon: Facebook, label: 'Facebook' },
  linkedin: { Icon: Linkedin, label: 'LinkedIn' },
  youtube: { Icon: Youtube, label: 'YouTube' },
};

// Derwent house watermark — big, bold white house filling the colour tile,
// matching the marketing brand mark.
const DerwentHouseWatermark = () => (
  <svg
    viewBox="0 0 200 180"
    aria-hidden
    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[86%] h-[86%] pointer-events-none"
    preserveAspectRatio="xMidYMid meet"
  >
    <g stroke="#FFFFFF" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" fill="none">
      {/* Outer roof chevron */}
      <path d="M18 78 L100 14 L182 78" />
      {/* Inner roof chevron + walls dropping down */}
      <path d="M42 100 L100 54 L158 100 L158 180" />
      <path d="M42 100 L42 180" />
    </g>
  </svg>
);

// Inline CSS keyframes — one-time injection so the featured card can pulse
// and the pinching hand emoji can "tap" repeatedly.
const DL_KEYFRAMES = `
@keyframes dl-pulse-card {
  0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0); }
  50%     { transform: scale(1.03); box-shadow: 0 10px 44px -4px rgba(255,255,255,0.4); }
}
@keyframes dl-tap-emoji {
  0%,100% { transform: scale(1) rotate(0deg) translateY(0); }
  40%     { transform: scale(0.82) rotate(-8deg) translateY(2px); }
  55%     { transform: scale(0.82) rotate(-8deg) translateY(2px); }
  70%     { transform: scale(1.08) rotate(2deg) translateY(-1px); }
}
.dl-featured-pulse { animation: dl-pulse-card 2.2s ease-in-out infinite; }
.dl-tap-hand        { animation: dl-tap-emoji 1.8s ease-in-out infinite; display: inline-block; }
.dl-card-glow       { box-shadow: 0 0 0 1px rgba(255,255,255,0.04), 0 4px 18px -2px rgba(255,255,255,0.10), 0 0 22px -6px rgba(255,255,255,0.18); }
.dl-card-glow:hover { box-shadow: 0 0 0 1px rgba(255,255,255,0.10), 0 8px 26px -2px rgba(255,255,255,0.22), 0 0 34px -4px rgba(255,255,255,0.30); }
`;

// (accent palette removed — grid tiles are now flat #f4f4f4)

function getSessionId() {
  try {
    let sid = sessionStorage.getItem('dl_sid');
    if (!sid) {
      sid = crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
      sessionStorage.setItem('dl_sid', sid);
    }
    return sid;
  } catch (_) { return null; }
}

async function track(evt) {
  try {
    await fetch(`${backendUrl}/api/derwent-links/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...evt, session_id: getSessionId(), referrer: document.referrer || null }),
      keepalive: true,
    });
  } catch (_) { /* silent */ }
}

// ─── Featured card — full width, strong contrast ─────────────────────────
const FeaturedCard = ({ item, onClick }) => {
  const Icon = ICON_MAP[item.icon] || Sparkles;
  const target = item.url && (item.url.startsWith('http') || item.url.startsWith('mailto:') || item.url.startsWith('tel:'))
    ? '_blank' : undefined;
  const hasCover = !!item.cover_image_url;

  return (
    <a
      href={item.url || '#'}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={() => onClick(item)}
      data-testid={`dl-link-${item.id}`}
      className="group relative block w-full overflow-hidden rounded-3xl transition-transform duration-300 hover:-translate-y-0.5 active:scale-[0.98] dl-featured-pulse"
      style={{ minHeight: hasCover ? '220px' : '170px', WebkitTapHighlightColor: 'transparent' }}
    >
      {hasCover ? (
        <>
          <img src={item.cover_image_url} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/20" />
        </>
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 100% at 100% 0%, rgba(255,255,255,0.14), transparent 60%),' +
              'linear-gradient(135deg, #FFFFFF 0%, #f4f4f4 100%)',
          }}
        />
      )}

      <div className={cn('relative h-full p-6 flex flex-col justify-end', hasCover ? 'text-white' : 'text-[#1d1d1d]')}>
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] uppercase tracking-[0.28em] font-bold opacity-70 mb-2">
              Featured
            </p>
            <h3
              className={cn('text-[24px] sm:text-[26px] font-bold leading-[1.1] tracking-tight', hasCover ? 'text-white' : 'text-[#1d1d1d]')}
              style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}
            >
              {item.label}
            </h3>
            {item.description && (
              <p className={cn('mt-2 text-[13.5px] font-medium leading-relaxed max-w-[85%]', hasCover ? 'text-white/85' : 'text-[#1d1d1d]/70')}>
                {item.description}
              </p>
            )}
          </div>
          {/* Tapping pinch-hand emoji CTA — animates like it's tapping */}
          <div
            className="shrink-0 select-none"
            style={{ fontSize: '48px', lineHeight: 1 }}
            aria-label="Tap to open"
          >
            <span className="dl-tap-hand" role="img" aria-hidden>🤏</span>
          </div>
        </div>
        {!hasCover && (
          <Icon className="absolute top-6 right-6 w-6 h-6 text-[#1d1d1d]/20" strokeWidth={1.6} />
        )}
      </div>
    </a>
  );
};

// ─── Standard grid card ── flat neutral tile (#f4f4f4) with dark text
const LinkCard = ({ item, onClick }) => {
  const Icon = ICON_MAP[item.icon] || Sparkles;
  const target = item.url && (item.url.startsWith('http') || item.url.startsWith('mailto:') || item.url.startsWith('tel:'))
    ? '_blank' : undefined;
  const hasCover = !!item.cover_image_url;

  return (
    <a
      href={item.url || '#'}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={() => onClick(item)}
      data-testid={`dl-link-${item.id}`}
      className="dl-card-glow group relative overflow-hidden rounded-2xl border border-white/10 hover:border-white/30 aspect-[2/1] cursor-pointer active:scale-[0.96] hover:-translate-y-0.5"
      style={{
        background: hasCover ? undefined : '#f4f4f4',
        transition: 'transform 0.15s ease, border-color 0.2s ease, box-shadow 0.25s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {hasCover && (
        <>
          <img src={item.cover_image_url} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/25" />
        </>
      )}

      <div className="relative h-full flex flex-col justify-between p-3.5">
        {/* Icon top-left + tap arrow top-right */}
        <div className="flex items-start justify-between">
          <Icon
            className="w-[22px] h-[22px]"
            strokeWidth={2}
            style={{ color: hasCover ? '#FFFFFF' : '#1d1d1d' }}
          />
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-transform duration-150 group-hover:scale-110 group-active:scale-95"
            style={{ background: hasCover ? 'rgba(255,255,255,0.14)' : '#1d1d1d' }}
            aria-hidden
          >
            <ArrowUpRight className="w-3.5 h-3.5 text-white" strokeWidth={2.4} />
          </span>
        </div>

        {/* Title + description bottom-left */}
        <div>
          <p
            className="text-[15px] font-bold leading-tight tracking-tight line-clamp-1"
            style={{
              fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
              color: hasCover ? '#FFFFFF' : '#1d1d1d',
            }}
          >
            {item.label}
          </p>
          {item.description && (
            <p className="text-[12px] mt-0.5 leading-snug line-clamp-1 font-medium"
               style={{ color: hasCover ? 'rgba(255,255,255,0.7)' : 'rgba(29,29,29,0.65)' }}>
              {item.description}
            </p>
          )}
        </div>
      </div>
    </a>
  );
};

// ─── Full-width row card (used when only one link in a category) ────────
const RowCard = ({ item, onClick }) => {
  const Icon = ICON_MAP[item.icon] || Sparkles;
  const target = item.url && (item.url.startsWith('http') || item.url.startsWith('mailto:') || item.url.startsWith('tel:'))
    ? '_blank' : undefined;

  return (
    <a
      href={item.url || '#'}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={() => onClick(item)}
      data-testid={`dl-link-${item.id}`}
      className="group flex items-center gap-4 p-4 rounded-2xl bg-[#f4f4f4] hover:bg-white transition-all"
      style={{ color: '#1d1d1d' }}
    >
      <div className="w-11 h-11 rounded-full bg-[#1d1d1d]/8 border border-[#1d1d1d]/15 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#1d1d1d]" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-[15px] font-bold leading-tight truncate"
          style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}
        >{item.label}</p>
        {item.description && (
          <p className="text-[12px] mt-0.5 truncate font-medium" style={{ color: 'rgba(29,29,29,0.65)' }}>{item.description}</p>
        )}
      </div>
      <div className="w-9 h-9 rounded-full bg-[#1d1d1d] flex items-center justify-center group-hover:scale-110 shrink-0 transition-transform">
        <ArrowUpRight className="w-4 h-4 text-white" strokeWidth={2.4} />
      </div>
    </a>
  );
};

// ─── Circular social icons ───────────────────────────────────────────────
const SocialCircles = ({ socials, onSocialClick }) => {
  if (!socials?.length) return null;
  return (
    <div className="flex items-center justify-center gap-3" data-testid="dl-socials">
      {socials.map((s) => {
        const meta = SOCIAL_META[s.platform];
        if (!meta) return null;
        const Icon = meta.Icon;
        return (
          <a
            key={s.platform}
            href={s.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onSocialClick(s)}
            aria-label={meta.label}
            data-testid={`dl-social-${s.platform}`}
            className="w-11 h-11 rounded-full bg-[#f4f4f4] hover:bg-white text-[#1d1d1d] flex items-center justify-center transition-all hover:scale-110"
          >
            <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
          </a>
        );
      })}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────
const CFG_CACHE_KEY = 'dl_cfg_v1';

function readCachedCfg() {
  try {
    const raw = localStorage.getItem(CFG_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

const DerwentLinksPage = () => {
  // Instant paint: render from the cached config immediately, then refresh
  // in the background (stale-while-revalidate).
  const [cfg, setCfg] = useState(readCachedCfg);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const chipRailRef = useRef(null);
  const pageViewSent = useRef(false);

  const load = useCallback(async () => {
    try {
      // Rely on the browser's HTTP cache + our SWR pattern below — this
      // keeps first paint instant when the endpoint is warm on the CDN.
      const res = await fetch(`${backendUrl}/api/derwent-links/public`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const fresh = await res.json();
      setCfg(fresh);
      try { localStorage.setItem(CFG_CACHE_KEY, JSON.stringify(fresh)); } catch (_) { /* quota */ }
    } catch (e) { if (!readCachedCfg()) setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load DM Sans + Inter as early as possible so the display name uses the
  // correct font on first paint. Runs once on mount, independent of cfg.
  useEffect(() => {
    // Preload the header logo + preconnect to the backend so the first
    // paint kicks off without waiting on the config JSON.
    if (!document.getElementById('dl-preload-logo')) {
      const preload = document.createElement('link');
      preload.id = 'dl-preload-logo';
      preload.rel = 'preload';
      preload.as = 'image';
      preload.href = '/derwent-links-logo.png';
      preload.fetchPriority = 'high';
      document.head.appendChild(preload);
    }
    if (backendUrl && !document.getElementById('dl-preconnect-api')) {
      const pc = document.createElement('link');
      pc.id = 'dl-preconnect-api';
      pc.rel = 'preconnect';
      pc.href = backendUrl;
      pc.crossOrigin = 'anonymous';
      document.head.appendChild(pc);
    }

    if (!document.getElementById('dl-font-dmsans')) {
      // Preconnect + font stylesheet
      const pc1 = document.createElement('link');
      pc1.rel = 'preconnect';
      pc1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(pc1);
      const pc2 = document.createElement('link');
      pc2.rel = 'preconnect';
      pc2.href = 'https://fonts.gstatic.com';
      pc2.crossOrigin = 'anonymous';
      document.head.appendChild(pc2);
      const link = document.createElement('link');
      link.id = 'dl-font-dmsans';
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=Inter:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!cfg) return;
    document.title = `${cfg.profile_name} — Links`;
    const setMeta = (name, content, attr = 'name') => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', cfg.profile_bio);
    setMeta('theme-color', '#1d1d1d');
    setMeta('og:title', `${cfg.profile_name} — Links`, 'property');
    setMeta('og:description', cfg.profile_bio, 'property');
    if (cfg.hero_image_url) setMeta('og:image', cfg.hero_image_url, 'property');
    else if (cfg.logo_url) setMeta('og:image', cfg.logo_url, 'property');

    // Pixel scripts
    const inject = (id, code, src) => {
      if (document.getElementById(id)) return;
      const s = document.createElement('script');
      s.id = id;
      if (src) { s.async = true; s.src = src; } else { s.text = code; }
      document.head.appendChild(s);
    };
    if (cfg.meta_pixel_id) {
      inject('dl-fbq', `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${cfg.meta_pixel_id}');fbq('track','PageView');`);
    }
    if (cfg.tiktok_pixel_id) {
      inject('dl-ttq', `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${cfg.tiktok_pixel_id}');ttq.page();}(window,document,'ttq');`);
    }
    if (cfg.ga_measurement_id) {
      inject('dl-gtag-src', null, `https://www.googletagmanager.com/gtag/js?id=${cfg.ga_measurement_id}`);
      inject('dl-gtag-init', `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${cfg.ga_measurement_id}');`);
    }

    if (!pageViewSent.current) {
      pageViewSent.current = true;
      track({ event_type: 'page_view', label: 'DerwentLinks' });
      try { window.fbq?.('track', 'PageView'); } catch (_) { /* noop */ }
      try { window.ttq?.track?.('PageView'); } catch (_) { /* noop */ }
      try { window.gtag?.('event', 'page_view'); } catch (_) { /* noop */ }
    }
  }, [cfg]);

  const handleLinkClick = useCallback((item) => {
    track({ event_type: 'click', link_id: item.id, label: item.label, url: item.url });
    try { window.fbq?.('track', 'Lead', { content_name: item.label }); } catch (_) { /* noop */ }
    try { window.ttq?.track?.('ClickButton', { content_name: item.label }); } catch (_) { /* noop */ }
    try { window.gtag?.('event', 'select_content', { content_type: 'link', item_id: item.id }); } catch (_) { /* noop */ }
  }, []);

  const handleSocialClick = useCallback((s) => {
    track({ event_type: 'social_click', link_id: s.platform, label: s.platform, url: s.url });
  }, []);

  // Derived data
  const enabledLinks = useMemo(() => (cfg?.links || []).filter((l) => l.enabled), [cfg]);
  const featured = useMemo(() => enabledLinks.find((l) => l.featured), [enabledLinks]);
  const nonFeatured = useMemo(() => enabledLinks.filter((l) => !l.featured), [enabledLinks]);
  const categories = useMemo(() => {
    const set = new Set();
    nonFeatured.forEach((l) => l.category && set.add(l.category));
    return ['All', ...Array.from(set)];
  }, [nonFeatured]);
  const visibleLinks = useMemo(() => {
    if (activeCategory === 'all' || activeCategory === 'All') return nonFeatured;
    return nonFeatured.filter((l) => l.category === activeCategory);
  }, [nonFeatured, activeCategory]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-black text-white">
        <div className="max-w-md text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] font-semibold mb-2 opacity-70">Derwent Links</p>
          <p className="text-sm opacity-70">Couldn&apos;t load — {error}</p>
        </div>
      </div>
    );
  }
  if (!cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-6 h-6 border border-white/40 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen antialiased text-white selection:bg-white/20"
      style={{
        background: '#1d1d1d',
        fontFamily: "'DM Sans', 'Inter', 'Helvetica Neue', system-ui, -apple-system, sans-serif",
      }}
      data-testid="derwent-links-page"
    >
      {/* CSS animations for the featured card pulse + tap emoji */}
      <style>{DL_KEYFRAMES}</style>

      {/* Very subtle grain overlay */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.04] mix-blend-overlay z-0"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E\")",
        }}
      />

      <main className="relative mx-auto max-w-[560px] pb-16 pt-10 sm:pt-14 px-5">
        {/* ── WHITE LOGO (wide) ─────────────────────────────────────────── */}
        <div className="flex items-center justify-center" data-testid="dl-header">
          <img
            src="/derwent-links-logo.png"
            alt={cfg.profile_name || 'Derwent Finance'}
            className="w-full max-w-[320px] sm:max-w-[380px] h-auto"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            width="380"
            height="109"
            data-testid="dl-logo"
          />
        </div>

        {/* ── PROFILE TEXT BLOCK (headline + bio + socials) ──────────── */}
        <section className="mt-6 text-center" data-testid="dl-profile">
          {cfg.profile_headline && (
            <p
              className="text-[15px] sm:text-base font-bold tracking-tight"
              style={{ color: '#FFFFFF', fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}
              data-testid="dl-headline"
            >
              {cfg.profile_headline}
            </p>
          )}
          {cfg.profile_bio && (
            <p
              className="mt-3 text-[13.5px] leading-relaxed max-w-[440px] mx-auto font-medium"
              style={{ color: 'rgba(255,255,255,0.85)' }}
              data-testid="dl-bio"
            >
              {cfg.profile_bio}
            </p>
          )}
          {cfg.profile_handle && (
            <p className="mt-2 text-[13px] text-white/60 font-medium" data-testid="dl-handle">
              {cfg.profile_handle}
            </p>
          )}

          {/* Socials */}
          <div className="mt-5">
            <SocialCircles socials={cfg.socials || []} onSocialClick={handleSocialClick} />
          </div>
        </section>

        {/* ── FEATURED CTA ────────────────────────────────────────────── */}
        {featured && (
          <section className="mt-8" data-testid="dl-featured">
            <FeaturedCard item={featured} onClick={handleLinkClick} />
          </section>
        )}

        {/* ── CATEGORY CHIPS (horizontal scroll) ──────────────────────── */}
        {categories.length > 1 && (
          <section className="mt-8 -mx-5" data-testid="dl-categories">
            <style>{`.dl-chip-rail::-webkit-scrollbar{display:none}`}</style>
            <div
              ref={chipRailRef}
              className="dl-chip-rail flex items-center gap-2 overflow-x-auto px-5 pb-1"
              style={{ scrollBehavior: 'smooth', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((cat) => {
                const isActive = activeCategory === cat || (activeCategory === 'all' && cat === 'All');
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    data-testid={`dl-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                    className={cn(
                      'shrink-0 px-4 py-1.5 rounded-full text-[12.5px] font-bold tracking-wide transition-all whitespace-nowrap',
                      isActive
                        ? 'bg-white text-[#1d1d1d] shadow-lg shadow-white/10'
                        : 'bg-[#f4f4f4] text-[#1d1d1d] hover:bg-white',
                    )}
                    style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ── LINK GRID (2 columns) ───────────────────────────────────── */}
        <section className="mt-5" data-testid="dl-grid">
          {visibleLinks.length === 0 ? (
            <p className="text-center text-white/50 text-sm py-10 font-medium">No links in this category.</p>
          ) : visibleLinks.length === 1 ? (
            <div className="space-y-3">
              <RowCard item={visibleLinks[0]} onClick={handleLinkClick} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {visibleLinks.map((item) => (
                <LinkCard key={item.id} item={item} onClick={handleLinkClick} />
              ))}
            </div>
          )}
        </section>

        {/* ── CONTACT ROW ─────────────────────────────────────────────── */}
        {(cfg.contact_phone || cfg.contact_email) && (
          <section className="mt-8" data-testid="dl-contact">
            <div className="grid grid-cols-2 gap-3">
              {cfg.contact_phone && (
                <a
                  href={`tel:${cfg.contact_phone.replace(/\s/g, '')}`}
                  onClick={() => handleLinkClick({ id: 'contact_phone', label: 'Call', url: `tel:${cfg.contact_phone}` })}
                  data-testid="dl-contact-phone"
                  className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-[#f4f4f4] text-[#1d1d1d] text-[13px] font-bold tracking-wide hover:bg-white transition-colors"
                  style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}
                >
                  <Phone className="w-4 h-4" strokeWidth={2.2} /> Call
                </a>
              )}
              {cfg.contact_email && (
                <a
                  href={`mailto:${cfg.contact_email}`}
                  onClick={() => handleLinkClick({ id: 'contact_email', label: 'Email', url: `mailto:${cfg.contact_email}` })}
                  data-testid="dl-contact-email"
                  className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-[#f4f4f4] text-[#1d1d1d] text-[13px] font-bold tracking-wide hover:bg-white transition-colors"
                  style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}
                >
                  <Mail className="w-4 h-4" strokeWidth={2.2} /> Email
                </a>
              )}
            </div>
          </section>
        )}

        {/* ── CALL US NOW — thin full-width tap-to-call bar ───────────── */}
        <section className="mt-8" data-testid="dl-call-now">
          <a
            href="tel:+61361449669"
            onClick={() => handleLinkClick({ id: 'call_us_now', label: 'Call Us Now', url: 'tel:+61361449669' })}
            data-testid="dl-call-now-btn"
            className="group relative flex items-center justify-center overflow-hidden rounded-2xl border border-white/[0.08] hover:border-white/25 cursor-pointer active:scale-[0.97] hover:-translate-y-0.5 py-3.5 shadow-lg shadow-black/20"
            style={{
              background:
                'radial-gradient(120% 160% at 50% 0%, rgba(16,185,129,0.92) 0%, rgba(16,185,129,0.55) 50%, rgba(16,185,129,0.28) 100%), #3A3670',
              transition: 'transform 0.15s ease, border-color 0.2s ease, box-shadow 0.2s ease',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <span
              className="text-[16px] font-bold text-white tracking-tight"
              style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}
            >
              Call Us Now ☎️
            </span>
          </a>
        </section>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <footer className="mt-14 text-center">
          <div className="space-y-1.5 text-[11.5px] text-white/55 font-medium" data-testid="dl-addresses">
            <p>19 Macquarie St, Hobart TAS 7000</p>
            <p>7/570 St Kilda Rd, Melbourne VIC 3004</p>
            <p>650/150 George St, Parramatta NSW 2150</p>
          </div>
          <p className="text-[11px] text-white/40 mt-4 font-medium" data-testid="dl-footer">
            {cfg.footer_text}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default DerwentLinksPage;
